import { gsap } from "gsap";

const PREFERS_REDUCE = window.matchMedia(
	"(prefers-reduced-motion: reduce)",
).matches;
const IS_MOBILE = window.matchMedia("(max-width: 760px)").matches;

/**
 * Apply per-element rotation from data-rot attribute (consistent, NOT random).
 * On mobile we skip rotation entirely (CSS handles it).
 */
function applyStaticRotations(): void {
	if (IS_MOBILE) return;
	const els = document.querySelectorAll<HTMLElement>("[data-rot]");
	els.forEach((el) => {
		const rot = Number.parseFloat(el.dataset.rot ?? "0");
		if (!Number.isFinite(rot) || rot === 0) return;
		el.style.transform = `rotate(${rot}deg)`;
	});
}

/**
 * Entrance: posters drop-in one-by-one, settle into their rotation.
 */
function runEntranceAnimation(): void {
	if (PREFERS_REDUCE) {
		applyStaticRotations();
		return;
	}

	const cards = document.querySelectorAll<HTMLElement>(
		".hero-cell, .m-card, .stat, .compare__col, .price-card, .faq-item, .foot",
	);
	if (cards.length === 0) return;

	cards.forEach((el) => {
		const rot = IS_MOBILE ? 0 : Number.parseFloat(el.dataset.rot ?? "0");
		gsap.set(el, { opacity: 0, y: 30, rotation: rot - 2 });
	});

	gsap.to(cards, {
		opacity: 1,
		y: 0,
		rotation: (_i, target: HTMLElement) => {
			if (IS_MOBILE) return 0;
			return Number.parseFloat(target.dataset.rot ?? "0");
		},
		duration: 0.55,
		ease: "power3.out",
		stagger: 0.06,
	});
}

/**
 * FAQ arrows draw on entrance via IntersectionObserver.
 */
function setupFaqArrowDraw(): void {
	const arrows = document.querySelectorAll<SVGSVGElement>(".faq-arrow");
	if (arrows.length === 0) return;
	if (PREFERS_REDUCE) {
		arrows.forEach((a) => a.classList.add("is-drawing"));
		return;
	}
	const io = new IntersectionObserver(
		(entries) => {
			entries.forEach((e) => {
				if (e.isIntersecting) {
					(e.target as SVGSVGElement).classList.add("is-drawing");
					io.unobserve(e.target);
				}
			});
		},
		{ threshold: 0.4 },
	);
	arrows.forEach((a) => io.observe(a));
}

/**
 * CTA press feedback (touch / click). CSS :active covers most, this adds
 * snap-back via a brief class for keyboard activation.
 */
function setupCtaPressFeedback(): void {
	const btns = document.querySelectorAll<HTMLElement>(".btn");
	btns.forEach((btn) => {
		btn.addEventListener("keydown", (e) => {
			if (e.key === "Enter" || e.key === " ") {
				btn.classList.add("is-pressed");
			}
		});
		btn.addEventListener("keyup", () => {
			btn.classList.remove("is-pressed");
		});
		btn.addEventListener("blur", () => {
			btn.classList.remove("is-pressed");
		});
	});
}

export function initMotion(): void {
	applyStaticRotations();
	runEntranceAnimation();
	setupFaqArrowDraw();
	setupCtaPressFeedback();
}
